import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { TransportMode, LanguageService, DangerousGoodService } from '@hazmatsuite-next/ui';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map, Observable, switchMap, take } from 'rxjs';
import { CompositePackagingModel } from '../../../models/composite-packaging.model';
import { InnerPackagingModel } from '../../../models/inner-packaging.model';
import { PackagingType } from '../../../models/packaging-type.model';
import { EasyDgdService } from '../../../services/easydgd.service';
import { InnerPackagingItemEditorComponent } from '../../inner-packaging-item-editor/inner-packaging-item-editor.component';
import * as uuid from 'uuid';
import { SinglePackagingModel } from '../../../models/single-packaging.model';
import { GefahrgutApiDangerousGoodItem } from '../../../models/gefahrgut-api-dangerous-good-item.interface';

@UntilDestroy()
@Component({
  selector: 'lib-easydgd-composite-packaging-modal',
  templateUrl: './composite-packaging-modal.component.html',
  styleUrls: ['./composite-packaging-modal.component.scss'],
})
export class CompositePackagingModalComponent implements OnInit {
  @ViewChild('innerPackaging', { static: false }) innerPackaging?: InnerPackagingItemEditorComponent;
  @Input() show = false;
  @Input() transportMode: TransportMode = TransportMode.Unknown;
  @Input() packagingUnCodes: PackagingType[] = [];
  @Input() packageTypes: PackagingType[] = [];
  @Input() set package(pkg: CompositePackagingModel) {
    if (!pkg) {
      this.model = new CompositePackagingModel();
    }

    this.countOfPackagings?.setValue(pkg.CountOfPackagings === 0 ? 1 : pkg.CountOfPackagings, { onlySelf: true });
    this.packagingType?.setValue(pkg.PackagingType, { onlySelf: true });
    this.packagingUnCode?.setValue((pkg.PackagingUnCode) || 0, { onlySelf: true });
    this.grossWeight?.setValue(pkg.GrossWeightValue, { onlySelf: true });
    this.isTotalQuantity?.setValue((pkg.IsTotalQuantity || false), { onlySelf: true });
    this.model = {
      ...pkg,
      Items: pkg.Items.map(item => {
        this.easyDgdService.setUuidIfNecessary(item);
        return item;
      })
    };
    this.easyDgdService.setUuidIfNecessary(this.model);
  };
  @Input() isEditMode = false;
  @Input() allDangerousGoods: GefahrgutApiDangerousGoodItem[] = [];

  @Output() closeModal = new EventEmitter<CompositePackagingModel>();
  @Output() loadUnPackagingTypes = new EventEmitter<number>();

  model: CompositePackagingModel = new CompositePackagingModel();
  formGroup: FormGroup;
  innerPackagingModalOpen = false;
  isEditModal = false;
  innerPackingIsValid = false;

  public singlePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['SinglePackagingResource']));

  public compositePackagingResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['CompositePackagingResource']));

  public validationResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['ValidationResource']));

  public packingListResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['PackingListResource']));
  public layoutResource$: Observable<any> = this.languageService.translations$.pipe(
    map(translations => translations['LayoutResource']));
  public currentInnerPackage: InnerPackagingModel = new InnerPackagingModel();
  public currentInnerPackageModel?: InnerPackagingModel;

  constructor(
    builder: FormBuilder,
    private languageService: LanguageService,
    private easyDgdService: EasyDgdService,
    private dangerousGoodService: DangerousGoodService,
    private cd: ChangeDetectorRef,
  ) {
    this.languageService.fetchResources(['SinglePackagingResource', 'CompositePackagingResource', 'ValidationResource', 'PackingListResource', 'LayoutResource']);

    this.formGroup = builder.group({
      countOfPackagings: new FormControl(1, [Validators.required, Validators.min(1)]),
      packagingType: new FormControl('4G', (ctrl: AbstractControl) =>
        Validators.required(ctrl)
      ),
      packagingUnCode: new FormControl(0, Validators.required),
      isTotalQuantity: new FormControl(false, Validators.required),
      grossWeight: new FormControl(null, [Validators.required, Validators.min(0.001)]),
    });
  }

  ngOnInit(): void {
    this.packagingType?.valueChanges.pipe(
      untilDestroyed(this)
    ).subscribe(val => {
      this.loadUnPackagingTypes.emit(val);
    });
  }

  editPackage(innerPackage: InnerPackagingModel): void {
    this.isEditModal = true;

    if (innerPackage.DangerousGoodId && !innerPackage.DangerousGood) {
      this.easyDgdService
        .initializeTempPackage(innerPackage.DangerousGoodId, innerPackage.PackageType, TransportMode.Rail)
        .pipe(
          switchMap(pkg => {
            const loadedPackage = pkg as SinglePackagingModel;
            return this.dangerousGoodService.getDangerousGood(loadedPackage.DangerousGoodId ?? 0, TransportMode.Rail).pipe(
              map(dangerousGood => {
                loadedPackage.DangerousGood = dangerousGood;
                return loadedPackage
              })
            )
          })
        ).subscribe((pkg: InnerPackagingModel) => {
          this.currentInnerPackage = this.mergeObjects<InnerPackagingModel>(innerPackage, pkg as InnerPackagingModel);
          this.openInnerPackagingModal();
        });
    } else {

      this.openInnerPackagingModal();
    }
    return;


  }

  currentInnerPackageChanged(pkg: InnerPackagingModel): void {
    this.currentInnerPackageModel = pkg;
  }

  addInnerPackaging(): void {
    this.currentInnerPackage = new InnerPackagingModel();
    this.openInnerPackagingModal();
  }

  removeInnerPackage(pkg: InnerPackagingModel): void {
    this.model.Items = this.model.Items.filter(item => item.Id !== pkg.Id);
  }

  duplicateInnerPackage(pkg: InnerPackagingModel): void {

    this.model.Items = [...this.model.Items, { ...pkg, Id: uuid.v4() }];
  }

  addPackage(): void {
    this.easyDgdService.getPackageDetails({
      ...this.currentInnerPackageModel,
      DangerousGood: undefined
    } as InnerPackagingModel, this.transportMode).pipe(
      take(1)
    ).subscribe(data => {
      data.DangerousGood = this.currentInnerPackageModel?.DangerousGood;
      data.DangerousGoodId = this.currentInnerPackageModel?.DangerousGoodId;
      this.model = {
        ...this.model,
        Items: [...this.model.Items, data]
      };

      this.cd.markForCheck();
      this.innerPackagingModalOpen = false;
      this.innerPackaging?.formGroup.reset();
    });
  }

  updatePackage(): void {
    this.model = {
      ...this.model,
      Items: [...this.model.Items.map(item => {
        if (item.Id === (this.currentInnerPackageModel as InnerPackagingModel).Id) {
          return this.currentInnerPackageModel as InnerPackagingModel;
        }
        return item;
      })]
    }
    this.innerPackagingModalOpen = false;
    this.innerPackaging?.formGroup.reset();
  }

  openInnerPackagingModal(): void {
    this.innerPackagingModalOpen = true;
    this.cd.detectChanges();
  }

  trackByCode(_: number, type: PackagingType) {
    return type.Id;
  }

  cancel() {
    this.closeModal.emit();
    this.formGroup?.reset({}, { emitEvent: false });
  }

  save() {
    if (this.formGroup.invalid) {
      return this.formGroup.markAllAsTouched();
    }

    this.closeModal.emit({
      ...this.model,
      CountOfPackagings: this.countOfPackagings?.value,
      PackagingType: this.packagingType?.value,
      PackagingUnCode: this.packagingUnCode?.value,
      IsTotalQuantity: this.isTotalQuantity?.value,
      GrossWeightValue: this.grossWeight?.value
    });
    this.formGroup?.reset({}, { emitEvent: false });
  }


  public changePackagingType(evt: any): void {
    this.packagingType?.setValue(evt.currentTarget.value, { onlySelf: true });
  }

  public changePackagingUnCode(evt: any): void {
    this.packagingUnCode?.setValue(evt.currentTarget.value, { onlySelf: true });
  }

  public markChildAsTouched(): void {
    this.innerPackaging?.markAsTouched();
  }

  public markAsTouched(): void {
    this.formGroup.markAllAsTouched();
  }

  get countOfPackagings(): AbstractControl | null {
    return this.formGroup?.get('countOfPackagings');
  }
  get packagingType(): AbstractControl | null {
    return this.formGroup?.get('packagingType');
  }
  get packagingUnCode(): AbstractControl | null {
    return this.formGroup?.get('packagingUnCode');
  }
  get grossWeight(): AbstractControl | null {
    return this.formGroup?.get('grossWeight');
  }
  get isTotalQuantity(): AbstractControl | null {
    return this.formGroup?.get('isTotalQuantity');
  }

  private mergeObjects<T extends object>(obj1: T, obj2: T): T {
    const result = { ...obj1 };

    for (const key in obj2) {
      // eslint-disable-next-line no-prototype-builtins
      if (obj2.hasOwnProperty(key)) {
        const value = obj2[key as keyof T];
        const originalValue = obj1[key as keyof T];

        // Only set the value from obj2 if it's not an empty string or undefined
        if (value) {
          result[key as keyof (T)] = value;
        } else {
          // Otherwise, retain the original value from obj1
          result[key as keyof (T)] = originalValue;
        }
      }
    }

    return result as T;
  }
}

